/**
 * FinMind API 股利資料服務
 * 用於獲取台股股利發放資訊
 */

import corsProxyService from './corsProxyService';
import { logger } from '../utils/logger';

export interface DividendData {
  date: string;                           // 除權日期 YYYY-MM-DD
  stockId: string;                        // 股票代號
  year: string;                          // 年度期別（如"112年第4季"）
  cashDividend: number;                  // 現金股利 (每股)
  stockDividend: number;                 // 股票股利 (每股)
  exDividendDate: string;                // 除息日 YYYY-MM-DD
  paymentDate: string;                   // 發放日 YYYY-MM-DD
  totalShares: number;                   // 參與分配總股數
  announcementDate: string;              // 公告日期 YYYY-MM-DD
  announcementTime?: string;             // 公告時間
}

export interface FinMindDividendResponse {
  msg: string;
  status: number;
  data: Array<{
    date: string;
    stock_id: string;
    year: string;
    StockEarningsDistribution: number;          // 股票股利 - 盈餘配股
    StockStatutorySurplus: number;              // 股票股利 - 法定盈餘配股
    StockExDividendTradingDate: string;         // 股票除權日
    TotalEmployeeStockDividend: number;         // 員工股票股利
    TotalEmployeeStockDividendAmount: number;   // 員工股票股利金額
    RatioOfEmployeeStockDividendOfTotal: number; // 員工股票股利占總數比例
    RatioOfEmployeeStockDividend: number;       // 員工股票股利比例
    CashEarningsDistribution: number;           // 現金股利 - 盈餘分配
    CashStatutorySurplus: number;               // 現金股利 - 法定盈餘
    CashExDividendTradingDate: string;          // 現金除息日
    CashDividendPaymentDate: string;            // 現金股利發放日
    TotalEmployeeCashDividend: number;          // 員工現金股利
    TotalNumberOfCashCapitalIncrease: number;   // 現金增資總額
    CashIncreaseSubscriptionRate: number;       // 現金增資認購比例
    CashIncreaseSubscriptionpRrice: number;     // 現金增資認購價格
    RemunerationOfDirectorsAndSupervisors: number; // 董監事酬勞
    ParticipateDistributionOfTotalShares: number;   // 參與分配總股數
    AnnouncementDate: string;                   // 公告日期
    AnnouncementTime?: string;                  // 公告時間
  }>;
}

export class DividendService {
  private static readonly FINMIND_API_BASE = 'https://api.finmindtrade.com/api/v4/data';
  private static readonly DATASET_TAIWAN_STOCK_DIVIDEND = 'TaiwanStockDividend';
  
  // 緩存股利資料 (60分鐘)
  private static cache = new Map<string, { data: DividendData[]; timestamp: number }>();
  private static readonly CACHE_DURATION = 60 * 60 * 1000; // 60分鐘

  /**
   * 獲取特定股票的股利資料
   */
  static async getStockDividend(
    stockId: string, 
    startDate: string = '2020-01-01',
    endDate?: string
  ): Promise<DividendData[]> {
    try {
      logger.dividend.info('Getting stock dividend data', { stockId, startDate, endDate });

      // 檢查緩存
      const cacheKey = `${stockId}_${startDate}_${endDate || 'latest'}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        logger.dividend.info('Retrieved dividend data from cache', { stockId, count: cached.length });
        return cached;
      }

      // 構建 API URL
      const params = new URLSearchParams({
        dataset: this.DATASET_TAIWAN_STOCK_DIVIDEND,
        data_id: stockId,
        start_date: startDate
      });

      if (endDate) {
        params.append('end_date', endDate);
      }

      const apiUrl = `${this.FINMIND_API_BASE}?${params.toString()}`;
      
      logger.dividend.info('Calling FinMind API for dividend data', { apiUrl });

      // 使用統一的 CORS 代理服務
      const response = await corsProxyService.fetch(apiUrl, {
        timeout: 30000 // 30秒超時，股利資料可能較多
      });

      if (!response.ok) {
        throw new Error(`FinMind API request failed: ${response.status} ${response.statusText}`);
      }

      const data: FinMindDividendResponse = await response.json();

      if (data.status !== 200) {
        throw new Error(`FinMind API error: ${data.msg}`);
      }

      // 解析和處理資料
      const dividends = this.parseDividendData(data.data);
      
      // 緩存結果
      this.setCachedData(cacheKey, dividends);

      logger.dividend.info('Successfully retrieved dividend data', { 
        stockId, 
        count: dividends.length,
        latestYear: dividends.length > 0 ? dividends[0].year : 'none'
      });

      return dividends;

    } catch (error) {
      logger.dividend.error('Failed to get stock dividend data', { 
        stockId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      
      // 返回空數組而不是拋出錯誤
      return [];
    }
  }

  /**
   * 獲取多支股票的股利資料
   */
  static async getMultipleStockDividends(
    stockIds: string[], 
    startDate: string = '2023-01-01',
    endDate?: string
  ): Promise<Record<string, DividendData[]>> {
    logger.dividend.info('Getting multiple stock dividend data', { 
      stockIds, 
      count: stockIds.length, 
      startDate, 
      endDate 
    });

    const results: Record<string, DividendData[]> = {};

    // 平行請求多支股票的股利資料
    const promises = stockIds.map(async (stockId) => {
      try {
        const dividends = await this.getStockDividend(stockId, startDate, endDate);
        return { stockId, dividends };
      } catch (error) {
        logger.dividend.warn('Failed to get dividend for stock', { stockId, error });
        return { stockId, dividends: [] };
      }
    });

    const responses = await Promise.all(promises);
    
    responses.forEach(({ stockId, dividends }) => {
      results[stockId] = dividends;
    });

    logger.dividend.info('Completed multiple stock dividend retrieval', { 
      requestedCount: stockIds.length, 
      successCount: Object.keys(results).length 
    });

    return results;
  }

  /**
   * 獲取近期股利發放資訊（過去1年）
   */
  static async getRecentDividends(stockIds: string[]): Promise<Record<string, DividendData[]>> {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const startDate = oneYearAgo.toISOString().split('T')[0];

    return await this.getMultipleStockDividends(stockIds, startDate);
  }

  /**
   * 解析 FinMind API 股利資料
   */
  private static parseDividendData(rawData: FinMindDividendResponse['data']): DividendData[] {
    return rawData
      .filter(item => {
        // 過濾出有實際股利發放的記錄
        const hasCashDividend = item.CashEarningsDistribution > 0 || item.CashStatutorySurplus > 0;
        const hasStockDividend = item.StockEarningsDistribution > 0 || item.StockStatutorySurplus > 0;
        return hasCashDividend || hasStockDividend;
      })
      .map(item => ({
        date: item.date,
        stockId: item.stock_id,
        year: item.year,
        cashDividend: (item.CashEarningsDistribution || 0) + (item.CashStatutorySurplus || 0),
        stockDividend: (item.StockEarningsDistribution || 0) + (item.StockStatutorySurplus || 0),
        exDividendDate: item.CashExDividendTradingDate || item.StockExDividendTradingDate || item.date,
        paymentDate: item.CashDividendPaymentDate || item.date,
        totalShares: item.ParticipateDistributionOfTotalShares || 0,
        announcementDate: item.AnnouncementDate,
        announcementTime: item.AnnouncementTime
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // 按日期降序排列
  }

  /**
   * 計算股票的歷年股利收益率
   */
  static calculateDividendYield(dividends: DividendData[], currentPrice: number): {
    latestYearYield: number;
    averageYield: number;
    totalDividendLastYear: number;
  } {
    if (dividends.length === 0 || currentPrice <= 0) {
      return { latestYearYield: 0, averageYield: 0, totalDividendLastYear: 0 };
    }

    // 計算最近一年的總股利
    const currentYear = new Date().getFullYear();
    const lastYearDividends = dividends.filter(d => {
      const dividendYear = new Date(d.date).getFullYear();
      return dividendYear >= currentYear - 1;
    });

    const totalDividendLastYear = lastYearDividends.reduce((sum, d) => sum + d.cashDividend, 0);
    const latestYearYield = (totalDividendLastYear / currentPrice) * 100;

    // 計算歷史平均收益率（過去3年）
    const threeYearsAgo = currentYear - 3;
    const recentDividends = dividends.filter(d => {
      const dividendYear = new Date(d.date).getFullYear();
      return dividendYear >= threeYearsAgo;
    });

    const yearlyTotals = new Map<number, number>();
    recentDividends.forEach(d => {
      const year = new Date(d.date).getFullYear();
      const current = yearlyTotals.get(year) || 0;
      yearlyTotals.set(year, current + d.cashDividend);
    });

    const averageDividend = Array.from(yearlyTotals.values()).reduce((sum, total) => sum + total, 0) / Math.max(yearlyTotals.size, 1);
    const averageYield = (averageDividend / currentPrice) * 100;

    return {
      latestYearYield: Number(latestYearYield.toFixed(2)),
      averageYield: Number(averageYield.toFixed(2)),
      totalDividendLastYear: Number(totalDividendLastYear.toFixed(2))
    };
  }

  /**
   * 獲取緩存資料
   */
  private static getCachedData(key: string): DividendData[] | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  /**
   * 設置緩存資料
   */
  private static setCachedData(key: string, data: DividendData[]): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });

    // 清理舊緩存（保持緩存大小合理）
    if (this.cache.size > 100) {
      const oldestKey = Array.from(this.cache.keys())[0];
      this.cache.delete(oldestKey);
    }
  }

  /**
   * 清除所有緩存
   */
  static clearCache(): void {
    this.cache.clear();
    logger.dividend.info('Dividend service cache cleared');
  }

  /**
   * 獲取服務狀態
   */
  static getServiceStatus() {
    return {
      cacheSize: this.cache.size,
      cacheDuration: this.CACHE_DURATION,
      apiEndpoint: this.FINMIND_API_BASE,
      dataset: this.DATASET_TAIWAN_STOCK_DIVIDEND
    };
  }

  /**
   * 測試 FinMind API 連接
   */
  static async testConnection(testStockId: string = '2330'): Promise<{
    success: boolean;
    responseTime: number;
    error?: string;
    sampleData?: DividendData[];
  }> {
    const startTime = Date.now();

    try {
      logger.dividend.info('Testing FinMind API connection', { testStockId });

      const dividends = await this.getStockDividend(testStockId, '2023-01-01');
      const responseTime = Date.now() - startTime;

      return {
        success: true,
        responseTime,
        sampleData: dividends.slice(0, 3) // 返回前3筆作為範例
      };

    } catch (error) {
      const responseTime = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.dividend.error('FinMind API connection test failed', { 
        testStockId, 
        error: errorMessage,
        responseTime 
      });

      return {
        success: false,
        responseTime,
        error: errorMessage
      };
    }
  }
}

// 匯出單例和類型
export default DividendService;

// 便利函數供組件使用
export const getDividendData = async (stockSymbol: string): Promise<DividendData[]> => {
  return await DividendService.getStockDividend(stockSymbol);
};

export const getMultipleDividendData = async (stockSymbols: string[]): Promise<Map<string, DividendData[]>> => {
  const results = await DividendService.getMultipleStockDividends(stockSymbols);
  const mapResults = new Map<string, DividendData[]>();
  Object.entries(results).forEach(([symbol, data]) => {
    mapResults.set(symbol, data);
  });
  return mapResults;
};

// 為了兼容性，創建一個別名接口
export interface DividendDataCompat {
  dividend_amount: number;       // 股利金額 (現金股利)
  dividend_date: string;         // 發放日
  ex_dividend_date: string;      // 除息日
  stock_id: string;             // 股票代號
  year: string;                 // 年度
}

// 轉換函數：將 DividendData 轉換為組件期望的格式
export const convertToCompatFormat = (data: DividendData[]): DividendDataCompat[] => {
  return data.map(item => ({
    dividend_amount: item.cashDividend,
    dividend_date: item.paymentDate,
    ex_dividend_date: item.exDividendDate,
    stock_id: item.stockId,
    year: item.year
  }));
};
